import api from '@/api/client';
import type {
  UpdateCompanyPayload,
  GetCompanyResponse,
  GetCompanyBusinessAddressesResponse,
  CompanyBusinessAddress,
  CreateCompanyBusinessAddress,
  UpdateCompanyBusinessAddress,
  UpdateCompanyOnboardingPayload,
  GetNotificationSettingsResponse,
} from '@/types/company';
import {
  OnboardingFeatureKey,
  type CompanyFeatureStatus,
  type GetCompanyFeaturesResponse,
  type UpdateCompanyFeaturesPayload,
  type UpdateCompanyFeaturesResponse,
} from '@gokrew/models/company';
import type {
  CompanyReadiness,
  GetCompanyReadinessResponse,
} from '@gokrew/models/readiness';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  ProbationSettings,
  ProbationSettingsPayload,
} from '@gokrew/models/company-dashboard';

export async function getCompanyById(companyId: string) {
  const { data } = await api.get<GetCompanyResponse>(`/companies/${companyId}`);
  return data;
}

export async function updateCompany(
  companyId: string,
  payload: UpdateCompanyPayload
) {
  const { data } = await api.patch(`/companies/${companyId}`, payload);
  return data;
}

export async function getNotificationSettings(companyId: string) {
  const { data } = await api.get<GetNotificationSettingsResponse>(
    `/companies/${companyId}/notification-settings`
  );
  return data.data;
}

export async function updateNotificationSettings(
  companyId: string,
  businessHoursOnly: boolean
) {
  const { data } = await api.put<GetNotificationSettingsResponse>(
    `/companies/${companyId}/notification-settings`,
    { businessHoursOnly }
  );
  return data.data;
}

/**
 * Probation settings: the Needs attention window (2026-10-11) and the
 * default probation length automatic reviews count from (2026-10-29).
 */
export async function getProbationSettings(companyId: string) {
  const { data } = await api.get<ApiResponse<ProbationSettings>>(
    `/companies/${companyId}/probation-settings`
  );
  return data.data;
}

export async function updateProbationSettings(
  companyId: string,
  payload: ProbationSettingsPayload
) {
  const { data } = await api.put<ApiResponse<ProbationSettings>>(
    `/companies/${companyId}/probation-settings`,
    payload
  );
  return data.data;
}

export async function completeCompanyOnboarding(companyId: string) {
  const { data } = await api.post<GetCompanyResponse>(
    `/companies/${companyId}/onboarding/complete`
  );
  return data;
}

export async function updateCompanyOnboarding(
  companyId: string,
  payload: UpdateCompanyOnboardingPayload
) {
  const { data } = await api.patch<GetCompanyResponse>(
    `/companies/${companyId}/onboarding`,
    payload
  );
  return data;
}

export async function getAllTimezones() {
  const { data } = await api.get('/information/timezones');
  return data.data;
}
export async function getCompanyBusinessAddresses(
  companyId: string
): Promise<GetCompanyBusinessAddressesResponse> {
  const { data } = await api.get(`/companies/${companyId}/business-addresses`);
  return data;
}

export async function getCompanyBusinessAddressById(
  companyId: string,
  id: string
): Promise<CompanyBusinessAddress> {
  const { data } = await api.get(
    `/companies/${companyId}/business-addresses/${id}`
  );
  return data;
}

export async function createCompanyBusinessAddress(
  companyId: string,
  payload: CreateCompanyBusinessAddress
): Promise<CompanyBusinessAddress> {
  const { data } = await api.post(
    `/companies/${companyId}/business-addresses`,
    payload
  );
  return data;
}

export async function updateCompanyBusinessAddress(
  companyId: string,
  id: string,
  payload: UpdateCompanyBusinessAddress
): Promise<CompanyBusinessAddress> {
  const { data } = await api.put(
    `/companies/${companyId}/business-addresses/${id}`,
    payload
  );
  return data;
}

export async function deleteCompanyBusinessAddress(
  companyId: string,
  id: string
): Promise<CompanyBusinessAddress> {
  const { data } = await api.delete(
    `/companies/${companyId}/business-addresses/${id}`
  );
  return data;
}

export async function getCompanyShiftPolicy(
  companyId: string,
  branchId?: string
) {
  const { data } = await api.get(`/companies/${companyId}/shift-policy`, {
    params: branchId ? { branchId } : undefined,
  });
  return data;
}

export interface ShiftPolicyPayload {
  shiftStart: string;
  shiftEnd: string;
  breakAllowanceMin: number;
  maxWorkingMin: number;
  autoApprove: boolean;
  requestFlowEnabled: boolean;
}

export async function updateCompanyShiftPolicy(
  companyId: string,
  payload: ShiftPolicyPayload,
  branchId?: string
) {
  const { data } = await api.patch(
    `/companies/${companyId}/shift-policy`,
    payload,
    {
      params: branchId ? { branchId } : undefined,
    }
  );
  return data;
}

export async function getCompanyFeatures(
  companyId: string
): Promise<CompanyFeatureStatus> {
  const { data } = await api.get<GetCompanyFeaturesResponse>(
    `/companies/${companyId}/features`
  );
  return data.data;
}

/** Disabled / not set up / ready / failing, per module and integration. */
export async function getCompanyReadiness(
  companyId: string
): Promise<CompanyReadiness> {
  const { data } = await api.get<GetCompanyReadinessResponse>(
    `/companies/${companyId}/readiness`
  );
  return data.data;
}

export async function updateCompanyFeatures(
  companyId: string,
  payload: UpdateCompanyFeaturesPayload
): Promise<CompanyFeatureStatus> {
  const { data } = await api.patch<UpdateCompanyFeaturesResponse>(
    `/companies/${companyId}/features`,
    payload
  );
  return data.data;
}

/**
 * Switches one module on, keeping the rest as they are. `enabledKeys` comes
 * from `useOptionalModules`: it carries timesheet only while timesheets run,
 * so enabling another module never starts them.
 */
export async function enableCompanyFeature(
  companyId: string,
  enabledKeys: OnboardingFeatureKey[],
  feature: OnboardingFeatureKey
): Promise<CompanyFeatureStatus> {
  return updateCompanyFeatures(companyId, {
    features: Array.from(new Set([...enabledKeys, feature])),
  });
}
